import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';

/* ─── tiny hook: count up when in view ─── */
const useCountUp = (target, duration = 1600, active = false) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    const num = parseFloat(String(target).replace(/[^\d.]/g, ''));
    const suffix = String(target).replace(/[\d.]/g, '');
    if (!num) { setVal(target); return; }
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      const current = ease * num;
      const decimals = (String(num).split('.')[1] || '').length;
      setVal(parseFloat(current.toFixed(decimals)) + suffix);
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [active, target, duration]);
  return val;
};

/* ─── stats data ─── */
const STATS = [
  { value: '500+', label: 'Restaurants Onboarded' },
  { value: '2M+',  label: 'Orders Processed' },
  { value: '99%',  label: 'Uptime Guaranteed' },
  { value: '4.9★', label: 'Average Rating' },
];

/* ─── features ─── */
const FEATURES = [
  {
    icon: '📲',
    title: 'QR Code Ordering',
    desc: 'Every table gets a unique QR code. Customers scan it, browse your digital menu instantly, and place orders right from their phone — no app download needed.',
    color: '#fff7ed',
    accent: '#ea580c',
  },
  {
    icon: '📦',
    title: 'Real-Time Order Management',
    desc: 'Every customer order lands directly on the admin dashboard with full details — items, quantity, table number, and special instructions. Nothing slips through.',
    color: '#f0fdf4',
    accent: '#16a34a',
  },
  {
    icon: '🎫',
    title: 'Custom Discount Coupons',
    desc: 'Admin can create and manage percentage or flat-rate discount coupons anytime. Set expiry dates, usage limits, and track redemptions from the dashboard.',
    color: '#fdf4ff',
    accent: '#9333ea',
  },
  {
    icon: '⭐',
    title: 'Customer Reviews',
    desc: 'Guests can leave item-level reviews directly from your restaurant page. Authentic feedback helps you improve quality and build customer trust.',
    color: '#fffbeb',
    accent: '#d97706',
  },
  {
    icon: '🪑',
    title: 'Pre-Reservations & Pre-Orders',
    desc: 'Customers can book a table in advance and even pre-order their meal. Full reservation and pre-order details arrive instantly in the admin panel.',
    color: '#eff6ff',
    accent: '#2563eb',
  },
  {
    icon: '🎨',
    title: 'Full UI/UX Customisation',
    desc: 'Admin can personalise the entire customer-facing experience — colours, logo, layout, menu categories, and more — all from one place, no developer needed.',
    color: '#f0fdfa',
    accent: '#0d9488',
  },
  {
    icon: '📊',
    title: 'Monthly Reports & Analytics',
    desc: 'Admin gets detailed monthly reports on revenue, top-selling items, order trends, peak hours, and coupon performance — all in one clean dashboard.',
    color: '#fff1f2',
    accent: '#e11d48',
  },
  {
    icon: '👥',
    title: 'Separate Staff & Admin Panels',
    desc: 'Staff gets a dedicated panel to manage their tasks and orders, while the admin panel gives owners full control over everything — isolated, secure, and role-based.',
    color: '#f8faff',
    accent: '#6366f1',
  },
  {
    icon: '💬',
    title: 'Direct Customer Messaging',
    desc: 'Customers can send queries or feedback directly from your restaurant page. Every message lands straight in the admin panel for quick, personal responses.',
    color: '#f0fdf4',
    accent: '#059669',
  },
];

/* ─── testimonials ─── */
const TESTIMONIALS = [
  {
    name: 'Rohit Mehra',
    role: 'Owner, The Spice Trail',
    text: 'The QR ordering system completely changed how our guests experience dining. No more waiting for menus — they scan, order, and we focus on the food.',
    avatar: 'RM',
    stars: 5,
  },
  {
    name: 'Ananya Kapoor',
    role: 'Operations Head, Café Deux',
    text: 'Pre-reservations with pre-orders means we\'re never caught off guard. The admin panel shows everything in real time — it\'s like having a control room for my restaurant.',
    avatar: 'AK',
    stars: 5,
  },
  {
    name: 'Suresh Iyer',
    role: 'Founder, Coastal Bites Chain',
    text: 'The monthly reports alone are worth it. I can see exactly what\'s selling, when, and why — and the coupon system drove a 30% spike in repeat visits.',
    avatar: 'SI',
    stars: 5,
  },
];

/* ─── customisation points ─── */
const CUSTOM_POINTS = [
  { icon: '🎨', title: 'Brand Identity', desc: 'Upload your logo, set your colour palette, and choose fonts — every page your customers see reflects your restaurant\'s unique personality.' },
  { icon: '🍴', title: 'Menu Your Way', desc: 'Organise categories, add item photos, set modifiers and portion sizes, toggle seasonal items on or off — full control over what guests see on the digital menu.' },
  { icon: '🪑', title: 'Table Layout & QR', desc: 'Configure your floor plan, assign QR codes per table, and let customers scan to order directly from their seat — a seamless dine-in experience.' },
  { icon: '🕐', title: 'Hours & Availability', desc: 'Set opening hours per day, mark holidays, and pause online orders instantly — no developer or technical knowledge required.' },
  { icon: '🎫', title: 'Offers & Coupons', desc: 'Create percentage or flat discounts, set expiry dates, limit usage, and track every redemption — designed to bring customers back.' },
  { icon: '📱', title: 'Customer Experience', desc: 'Customise the ordering page look, confirmation messages, receipt layout, and notification emails — so every touchpoint feels like your brand.' },
];

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const LandingPage = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  /* nav scroll effect */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 320);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: '#ffffff', color: '#111827', overflowX: 'hidden' }}>

      {/* ── Google Font ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Playfair+Display:wght@700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { overflow-x: hidden; }
        .btn-primary {
          background: #111827; color: #fff; border: none; border-radius: 10px;
          padding: 13px 28px; font-size: 15px; font-weight: 600; cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s, background 0.2s;
          font-family: inherit;
        }
        .btn-primary:hover { background: #1f2937; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(17,24,39,0.18); }
        .btn-outline {
          background: transparent; color: #111827; border: 1.5px solid #d1d5db;
          border-radius: 10px; padding: 12px 26px; font-size: 15px; font-weight: 600;
          cursor: pointer; transition: all 0.2s; font-family: inherit;
        }
        .btn-outline:hover { border-color: #111827; background: #f9fafb; }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes pulse-ring { 0%{transform:scale(1);opacity:0.6} 100%{transform:scale(1.35);opacity:0} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes fadeSlideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* ══════════ NAV ══════════ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        borderBottom: scrolled ? '1px solid #f3f4f6' : 'none',
        transition: 'all 0.3s',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src="/favicon.svg" alt="Logo" style={{ width: 36, height: 36, objectFit: 'contain' }} />
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>SwadZone</span>
          </div>

          {/* Desktop nav */}
          <div style={{ display: 'flex', gap: 32, alignItems: 'center' }} className="desktop-nav">
            {[['Features', 'features'], ['Customise', 'customise'], ['Testimonials', 'testimonials'], ['FAQ', 'faq'], ['Enquiry', 'enquiry']].map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} style={{ background: 'none', border: 'none', fontSize: 14, fontWeight: 500, color: '#374151', cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = '#111827'}
                onMouseLeave={e => e.target.style.color = '#374151'}
              >{label}</button>
            ))}
          </div>

          {/* CTA */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="btn-outline nav-cta-hide" onClick={() => navigate('/super-admin/login')} style={{ padding: '9px 18px', fontSize: 13 }}>Admin Login</button>
            <button className="btn-primary nav-cta-hide" onClick={() => scrollTo('enquiry')} style={{ padding: '9px 18px', fontSize: 13 }}>Get Started</button>
            {/* Hamburger */}
            <button onClick={() => setMenuOpen(!menuOpen)} style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 8 }} className="hamburger">
              <div style={{ width: 24, height: 2, background: '#111827', borderRadius: 2, marginBottom: 5, transition: 'all 0.25s', transform: menuOpen ? 'rotate(45deg) translateY(7px)' : 'none' }} />
              <div style={{ width: 24, height: 2, background: '#111827', borderRadius: 2, marginBottom: 5, opacity: menuOpen ? 0 : 1, transition: 'opacity 0.2s' }} />
              <div style={{ width: 24, height: 2, background: '#111827', borderRadius: 2, transition: 'all 0.25s', transform: menuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none' }} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              style={{ background: '#fff', borderTop: '1px solid #f3f4f6', overflow: 'hidden' }}>
              <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {[['Features', 'features'], ['Customise', 'customise'], ['Testimonials', 'testimonials'], ['FAQ', 'faq'], ['Enquiry', 'enquiry']].map(([label, id]) => (
                  <button key={id} onClick={() => scrollTo(id)} style={{ background: 'none', border: 'none', padding: '10px 0', fontSize: 15, fontWeight: 500, color: '#374151', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>{label}</button>
                ))}
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <button className="btn-outline" onClick={() => navigate('/super-admin/login')} style={{ flex: 1, fontSize: 14 }}>Admin Login</button>
                  <button className="btn-primary" onClick={() => scrollTo('enquiry')} style={{ flex: 1, fontSize: 14 }}>Get Started</button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* responsive nav hide/show + full mobile styles */}
      <style>{`
        /* ── Nav ── */
        @media(max-width:768px){
          .desktop-nav{display:none!important}
          .hamburger{display:block!important}
          .nav-cta-hide{display:none!important}
        }
        /* ── Hero ── */
        @media(max-width:768px){
          .hero-grid{grid-template-columns:1fr!important; gap:32px!important; padding:48px 16px 40px!important}
          .hero-mockup{display:none!important}
          .hero-btns{flex-direction:column!important}
          .hero-btns button{width:100%!important; text-align:center!important}
        }
        /* ── Stats ── */
        @media(max-width:640px){
          .stats-grid{grid-template-columns:repeat(2,1fr)!important; padding:36px 16px!important}
        }
        /* ── Features ── */
        @media(max-width:640px){
          .features-grid{grid-template-columns:1fr!important}
        }
        /* ── How it Works ── */
        @media(max-width:640px){
          .steps-grid{grid-template-columns:1fr!important}
        }
        /* ── Testimonials ── */
        @media(max-width:640px){
          .testimonials-grid{grid-template-columns:1fr!important}
        }
        /* ── Customise ── */
        @media(max-width:768px){
          .customise-split{grid-template-columns:1fr!important; gap:32px!important}
          .customise-grid{grid-template-columns:1fr!important}
        }
        /* ── Enquiry Form ── */
        @media(max-width:600px){
          .enquiry-form-grid{grid-template-columns:1fr!important}
          .enquiry-card{padding:24px 16px!important}
        }
        /* ── CTA ── */
        @media(max-width:480px){
          .cta-btns{flex-direction:column!important; align-items:stretch!important}
          .cta-btns button{width:100%!important}
        }
        /* ── Footer ── */
        @media(max-width:600px){
          .footer-inner{flex-direction:column!important; text-align:center!important; gap:12px!important}
        }
        /* ── Section padding on mobile ── */
        @media(max-width:600px){
          section{padding-left:16px!important; padding-right:16px!important}
          section > div{padding-left:0!important; padding-right:0!important}
        }
        /* ── Touch targets & input zoom fix ── */
        @media(max-width:768px){
          button{min-height:44px}
          input, textarea, select{font-size:16px!important}
        }
        /* ── Mobile menu backdrop fix ── */
        @media(max-width:768px){
          nav{background:rgba(255,255,255,0.98)!important; backdrop-filter:blur(14px)!important}
        }
      `}</style>

      {/* ══════════ HERO ══════════ */}
      <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', paddingTop: 68, background: 'linear-gradient(160deg, #ffffff 55%, #f8faff 100%)', position: 'relative', overflow: 'hidden' }}>
        {/* subtle grid bg */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(#e5e7eb 1px, transparent 1px)', backgroundSize: '28px 28px', opacity: 0.45, pointerEvents: 'none' }} />
        {/* decorative blobs */}
        <div style={{ position: 'absolute', top: '10%', right: '-5%', width: 500, height: 500, background: 'radial-gradient(circle, #dbeafe 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '0%', left: '-8%', width: 400, height: 400, background: 'radial-gradient(circle, #fef3c7 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

        <div className="hero-grid" style={{ maxWidth: 1200, margin: '0 auto', padding: '80px 24px', position: 'relative', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
          {/* Left */}
          <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 500, color: '#166534', marginBottom: 28 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a', display: 'inline-block', animation: 'pulse-ring 1.4s ease-out infinite' }} />
              Now serving 500+ restaurants across India
            </div>

            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(38px, 5vw, 62px)', fontWeight: 800, lineHeight: 1.1, color: '#0f172a', marginBottom: 24, letterSpacing: '-1px' }}>
              Your Restaurant. <br />
              <span style={{ color: '#6366f1', fontStyle: 'italic' }}>Fully in Your Hands.</span>
            </h1>

            <p style={{ fontSize: 18, color: '#4b5563', lineHeight: 1.7, maxWidth: 480, marginBottom: 40 }}>
              QR ordering, live order management, pre-reservations, custom coupons, customer reviews, monthly reports, and complete UI customisation — all controlled by you from one powerful admin panel.
            </p>

            <div className="hero-btns" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 48 }}>
              <button className="btn-primary" onClick={() => scrollTo('enquiry')} style={{ fontSize: 16, padding: '14px 32px', borderRadius: 12 }}>
                Start Free Trial →
              </button>
              <button className="btn-outline" onClick={() => scrollTo('features')} style={{ fontSize: 16, padding: '14px 32px', borderRadius: 12 }}>
                Explore Features
              </button>
            </div>

            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
              {['No credit card required', 'Setup in 2 hours', 'Full admin control'].map(t => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#6b7280' }}>
                  <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span> {t}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right — animated live dashboard mockup */}
          <motion.div className="hero-mockup" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: 'flex', justifyContent: 'center', animation: 'float 4s ease-in-out infinite' }}>
            <LiveDashboardMockup />
          </motion.div>
        </div>
      </section>

      {/* ══════════ STATS BAR ══════════ */}
      <StatsBar />

      {/* ══════════ FEATURES ══════════ */}
      <section id="features" style={{ padding: '100px 24px', background: '#fafafa' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SectionHeading
            tag="Features"
            title="Everything your restaurant needs"
            sub="From QR table ordering to monthly analytics — every feature is built for restaurant owners, managed entirely from your admin panel."
          />
          <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20, marginTop: 56 }}>
            {FEATURES.map((f, i) => <FeatureCard key={f.title} data={f} delay={i * 0.07} />)}
          </div>
        </div>
      </section>

      {/* ══════════ HOW IT WORKS ══════════ */}
      <section style={{ padding: '100px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <SectionHeading tag="How It Works" title="Up and running in 3 steps" sub="No technical knowledge needed. We handle the setup — you handle the food." />
          <div className="steps-grid" style={{ marginTop: 60, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 32 }}>
            {[
              { num: '01', title: 'Create Your Account', desc: 'Sign up as Super Admin. Add your restaurant, set your brand, and configure your basic details in minutes.', icon: '✍️' },
              { num: '02', title: 'Set Up Your Panel', desc: 'Upload your menu, generate table QR codes, configure reservations, create coupons, and customise your customer-facing page — all without any developer.', icon: '🛠️' },
              { num: '03', title: 'Go Live & Take Control', desc: 'Share your restaurant link, place QR codes on tables, and start receiving orders, reservations, and customer messages — all managed from one dashboard.', icon: '🚀' },
            ].map((step, i) => (
              <motion.div key={step.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                whileHover={{ y: -6, boxShadow: '0 14px 36px rgba(99,102,241,0.10)', transition: { duration: 0.22 } }}
                style={{ textAlign: 'center', padding: '32px 24px', background: '#fafafa', borderRadius: 18, border: '1px solid #f1f5f9', position: 'relative', cursor: 'default' }}>
                <motion.div
                  whileHover={{ scale: 1.2, rotate: 10, transition: { type: 'spring', stiffness: 300 } }}
                  style={{ fontSize: 36, marginBottom: 14, display: 'inline-block' }}>{step.icon}</motion.div>
                <div style={{ position: 'absolute', top: 20, right: 20, fontSize: 13, fontWeight: 700, color: '#e5e7eb', fontFamily: "'Playfair Display',serif" }}>{step.num}</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 10 }}>{step.title}</h3>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.65 }}>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ TESTIMONIALS ══════════ */}
      <section id="testimonials" style={{ padding: '100px 24px', background: '#0f172a' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionHeading tag="Testimonials" title="Restaurant owners love it" sub="Real results from real businesses — straight from the people using it every day." dark />
          <div className="testimonials-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20, marginTop: 56 }}>
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={t.name}
                initial={{ opacity: 0, y: 32, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6, scale: 1.02, borderColor: '#4f46e580', transition: { duration: 0.22 } }}
                style={{ background: '#1e293b', borderRadius: 18, padding: '28px 28px', border: '1px solid #334155', cursor: 'default' }}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
                  {Array(t.stars).fill(0).map((_, j) => (
                    <motion.span key={j}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.12 + j * 0.06 + 0.3, type: 'spring', stiffness: 400 }}
                      style={{ color: '#fbbf24', fontSize: 15 }}>★</motion.span>
                  ))}
                </div>
                <p style={{ fontSize: 15, color: '#cbd5e1', lineHeight: 1.7, marginBottom: 22, fontStyle: 'italic' }}>"{t.text}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <motion.div
                    whileHover={{ scale: 1.12, transition: { duration: 0.2 } }}
                    style={{ width: 40, height: 40, borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{t.avatar}</motion.div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#f8fafc' }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ CUSTOMISATION ══════════ */}
      <section id="customise" style={{ padding: '100px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div className="customise-split" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center', marginBottom: 72 }}>
            <div>
              <div style={{ display: 'inline-block', background: '#fdf4ff', color: '#7e22ce', border: '1px solid #e9d5ff', borderRadius: 999, padding: '5px 16px', fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 20 }}>Built Around You</div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(26px,3.2vw,42px)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, letterSpacing: '-0.5px', marginBottom: 20 }}>
                Every restaurant is different.<br /><span style={{ color: '#6366f1', fontStyle: 'italic' }}>So is your setup.</span>
              </h2>
              <p style={{ fontSize: 16, color: '#4b5563', lineHeight: 1.75, marginBottom: 28 }}>
                Your admin panel is your command centre. Change your menu, redesign your ordering page, create discount coupons, configure table QR codes, manage staff access, and view monthly reports — all without touching a single line of code. Everything updates live, instantly.
              </p>
              {['Personalise your brand colours, logo, and layout', 'Update menus, offers, and hours in real time', 'Separate access for admin and staff — fully role-based'].map(t => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#374151', marginBottom: 10 }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#f0fdf4', border: '1.5px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11, color: '#16a34a', fontWeight: 700 }}>✓</div>
                  {t}
                </div>
              ))}
            </div>
            <div style={{ background: '#fafafa', borderRadius: 20, border: '1px solid #f1f5f9', padding: '28px 28px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>Your Restaurant Panel</div>
              {[
                { label: 'Restaurant Name', val: 'The Golden Fork', col: '#6366f1' },
                { label: 'Brand Colour', val: '● Amber Gold #D4872A', col: '#d97706' },
                { label: 'Active Menu Items', val: '48 items · 6 categories', col: '#16a34a' },
                { label: 'Tables Configured', val: '22 tables · 3 sections', col: '#2563eb' },
                { label: 'Current Offer', val: '20% off weekday lunch', col: '#9333ea' },
                { label: 'Order Page', val: 'Live — accepting orders', col: '#16a34a' },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fff', borderRadius: 10, border: '1px solid #f1f5f9', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>{row.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: row.col }}>{row.val}</span>
                </div>
              ))}
            </div>
          </motion.div>
          <div className="customise-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 18 }}>
            {CUSTOM_POINTS.map((pt, i) => (
              <motion.div key={pt.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.55 }}
                whileHover={{ y: -5, scale: 1.02, boxShadow: '0 12px 32px rgba(99,102,241,0.10)', transition: { duration: 0.22 } }}
                style={{ background: '#fafafa', borderRadius: 16, padding: '26px 26px', border: '1px solid #f1f5f9', cursor: 'default' }}>
                <motion.div
                  whileHover={{ rotate: [0, -10, 10, 0], transition: { duration: 0.4 } }}
                  style={{ fontSize: 26, marginBottom: 14, display: 'inline-block' }}>{pt.icon}</motion.div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>{pt.title}</h3>
                <p style={{ fontSize: 13.5, color: '#6b7280', lineHeight: 1.7 }}>{pt.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FAQ ══════════ */}
      <section id="faq" style={{ padding: '100px 24px', background: '#fafafa' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <SectionHeading tag="FAQ" title="Common questions" sub="Can't find an answer? Reach out to us directly." />
          <div style={{ marginTop: 48, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { q: 'How does the QR ordering system work?', a: 'Each table in your restaurant gets a unique QR code generated from your admin panel. When a customer scans it, your digital menu opens instantly in their browser — no app download required. They browse, select items, and place an order, which arrives in your admin panel in real time.' },
              { q: 'Does the admin receive full order details?', a: 'Yes. Every order placed by a customer — whether dine-in via QR or online — comes with complete details: customer name, table number, items ordered, quantities, special instructions, and timestamp. All visible in your admin dashboard immediately.' },
              { q: 'Can I create custom discount coupons?', a: 'Absolutely. From your admin panel you can create coupon codes with percentage or flat discounts, set expiry dates, limit the number of uses, and track how many times each coupon has been redeemed.' },
              { q: 'How do pre-reservations and pre-orders work?', a: 'Customers can book a table in advance directly from your restaurant page and optionally pre-select their meal. You receive all reservation details — date, time, party size, and pre-ordered items — in your admin panel so you can prepare ahead.' },
              { q: 'Is there a separate login for staff and admin?', a: 'Yes. Admin and staff have completely separate logins with different access levels. Staff can manage their tasks and assigned orders, while the admin has full control over the entire restaurant — menu, reports, coupons, reservations, and more.' },
              { q: 'How are customer messages received by the admin?', a: 'Customers can send a query or feedback directly from your restaurant page using the built-in contact feature. Every message is delivered straight to the admin panel, allowing you to respond promptly and personally.' },
              { q: 'What does the monthly report include?', a: 'Monthly reports cover total revenue, order volume, top-selling items, peak hours, coupon redemption stats, and customer activity trends — giving you clear insights to make smarter decisions each month.' },
              { q: 'How long does the initial setup take?', a: 'Most restaurants are fully live within 2 hours. The setup guide walks you through adding your menu, generating QR codes, configuring tables, and customising your page — step by step, no technical help needed.' },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  style={{ width: '100%', padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, fontFamily: 'inherit' }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#111827', textAlign: 'left' }}>{item.q}</span>
                  <span style={{ fontSize: 20, color: '#9ca3af', flexShrink: 0, transition: 'transform 0.2s', transform: activeFaq === i ? 'rotate(45deg)' : 'none' }}>+</span>
                </button>
                <AnimatePresence>
                  {activeFaq === i && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} style={{ overflow: 'hidden' }}>
                      <div style={{ padding: '0 24px 20px', fontSize: 14, color: '#4b5563', lineHeight: 1.7 }}>{item.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ ENQUIRY FORM ══════════ */}
      <EnquiryForm />

      {/* ══════════ CTA BANNER ══════════ */}
      <section id="contact" style={{ padding: '80px 24px', background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)' }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 20 }}>🚀</div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(28px,4vw,48px)', fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: 16 }}>
            Ready to transform your restaurant?
          </h2>
          <p style={{ fontSize: 17, color: '#94a3b8', marginBottom: 36, lineHeight: 1.6 }}>
            Join 500+ restaurants already running smarter — with QR ordering, pre-reservations, custom coupons, live order management, and full admin control. Start your free trial today.
          </p>
          <div className="cta-btns" style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={() => scrollTo('enquiry')} style={{ fontSize: 16, padding: '14px 36px', borderRadius: 12, background: '#6366f1' }}>
              Start Free Trial
            </button>
            
          </div>
        </motion.div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer style={{ background: '#0f172a', borderTop: '1px solid #1e293b', padding: '40px 24px' }}>
        <div className="footer-inner" style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/favicon.svg" alt="Logo" style={{ width: 36, height: 36, objectFit: 'contain' }} />
            <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, color: '#f8fafc' }}>SwadZone</span>
          </div>
          <div style={{ fontSize: 13, color: '#64748b' }}>© {new Date().getFullYear()} SwadZone. All rights reserved.</div>
          <button onClick={() => navigate('/super-admin/login')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#64748b', fontFamily: 'inherit', transition: 'color 0.2s' }}
            onMouseEnter={e => e.target.style.color = '#94a3b8'}
            onMouseLeave={e => e.target.style.color = '#64748b'}>
            Super Admin →
          </button>
        </div>
      </footer>

    </div>
  );
};

/* ── Sub components ── */

const StatsBar = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <div ref={ref} style={{ background: '#0f172a', padding: '52px 24px' }}>
      <div className="stats-grid" style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20 }}>
        {STATS.map((s, i) => <StatItem key={s.label} stat={s} delay={i * 0.1} inView={inView} />)}
      </div>
    </div>
  );
};

const StatItem = ({ stat, delay, inView }) => {
  const val = useCountUp(stat.value, 1600, inView);
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay, duration: 0.6 }}
      style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(28px,3vw,42px)', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
        {inView ? val : '0'}
      </div>
      <div style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>{stat.label}</div>
    </motion.div>
  );
};

const SectionHeading = ({ tag, title, sub, dark }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
    style={{ textAlign: 'center', maxWidth: 620, margin: '0 auto' }}>
    <div style={{ display: 'inline-block', background: dark ? '#1e293b' : '#f0f9ff', color: dark ? '#94a3b8' : '#2563eb', border: `1px solid ${dark ? '#334155' : '#bfdbfe'}`, borderRadius: 999, padding: '5px 16px', fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 18 }}>{tag}</div>
    <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(26px,3.5vw,44px)', fontWeight: 800, color: dark ? '#f8fafc' : '#0f172a', lineHeight: 1.2, marginBottom: 16, letterSpacing: '-0.5px' }}>{title}</h2>
    <p style={{ fontSize: 17, color: dark ? '#64748b' : '#6b7280', lineHeight: 1.65 }}>{sub}</p>
  </motion.div>
);

/* ── Enquiry Form Component ── */
const EnquiryForm = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', restaurant: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Please enter a valid email';
    if (!form.phone.trim() || !/^\d{10}$/.test(form.phone.replace(/\s/g, ''))) e.phone = 'Please enter a 10-digit phone number';
    if (!form.restaurant.trim()) e.restaurant = 'Restaurant name is required';
    if (!form.message.trim()) e.message = 'Message is required';
    return e;
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: undefined }));
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);

    // ── EmailJS config — apne values yahan daalo ──
    const SERVICE_ID  = 'service_nq5b858';   // EmailJS Service ID
    const TEMPLATE_ID = 'template_lgo1icm';  // EmailJS Template ID
    const PUBLIC_KEY  = 'DBaQqo5BwAkIeT8Ji';   // EmailJS Public Key

    const templateParams = {
      from_name:       form.name,
      from_email:      form.email,
      phone:           form.phone,
      restaurant_name: form.restaurant,
      message:         form.message,
    };

    emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY)
      .then(() => {
        setLoading(false);
        setSubmitted(true);
      })
      .catch((err) => {
        console.error('EmailJS error:', err);
        setLoading(false);
        alert('Something went wrong. Please try again or contact us directly.');
      });
  };

  const inputStyle = (field) => ({
    width: '100%', padding: '13px 16px', borderRadius: 10, border: `1.5px solid ${errors[field] ? '#f87171' : '#e5e7eb'}`,
    fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', background: '#fff',
    transition: 'border-color 0.2s',
  });

  return (
    <section id="enquiry" style={{ padding: '100px 24px', background: '#f8faff' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <SectionHeading
          tag="Enquiry"
          title="Get in touch with us"
          sub="Tell us about your restaurant — we'll walk you through everything and suggest the best setup for your needs."
        />

        <motion.div
          className="enquiry-card"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          style={{ marginTop: 48, background: '#fff', borderRadius: 24, border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(99,102,241,0.07)', padding: '40px 40px' }}
        >
          {submitted ? (
            <motion.div initial={{ opacity: 0, scale: 0.93 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
              style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 56, marginBottom: 20 }}>🎉</div>
              <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>
                Enquiry Submitted!
              </h3>
              <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7, marginBottom: 28 }}>
                Thank you! Our team will contact you within <strong>24 hours</strong>.<br />
                In the meantime, keep exploring SwadZone. 🍴
              </p>
              <button className="btn-primary" onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', restaurant: '', message: '' }); }}
                style={{ background: '#6366f1', borderRadius: 10, padding: '11px 28px' }}>
                Submit Another Enquiry
              </button>
            </motion.div>
          ) : (
            <div>
              {/* Row 1: Name + Email */}
              <div className="enquiry-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Your Name *</label>
                  <input
                    type="text" placeholder="Rohit Mehra"
                    value={form.name} onChange={e => handleChange('name', e.target.value)}
                    style={inputStyle('name')}
                    onFocus={e => e.target.style.borderColor = '#6366f1'}
                    onBlur={e => e.target.style.borderColor = errors.name ? '#f87171' : '#e5e7eb'}
                  />
                  {errors.name && <p style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{errors.name}</p>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Email *</label>
                  <input
                    type="email" placeholder="rohit@example.com"
                    value={form.email} onChange={e => handleChange('email', e.target.value)}
                    style={inputStyle('email')}
                    onFocus={e => e.target.style.borderColor = '#6366f1'}
                    onBlur={e => e.target.style.borderColor = errors.email ? '#f87171' : '#e5e7eb'}
                  />
                  {errors.email && <p style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{errors.email}</p>}
                </div>
              </div>

              {/* Row 2: Phone + Restaurant */}
              <div className="enquiry-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Phone Number *</label>
                  <input
                    type="tel" placeholder="98765 43210"
                    value={form.phone} onChange={e => handleChange('phone', e.target.value)}
                    style={inputStyle('phone')}
                    onFocus={e => e.target.style.borderColor = '#6366f1'}
                    onBlur={e => e.target.style.borderColor = errors.phone ? '#f87171' : '#e5e7eb'}
                  />
                  {errors.phone && <p style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{errors.phone}</p>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Restaurant Name *</label>
                  <input
                    type="text" placeholder="The Spice Trail"
                    value={form.restaurant} onChange={e => handleChange('restaurant', e.target.value)}
                    style={inputStyle('restaurant')}
                    onFocus={e => e.target.style.borderColor = '#6366f1'}
                    onBlur={e => e.target.style.borderColor = errors.restaurant ? '#f87171' : '#e5e7eb'}
                  />
                  {errors.restaurant && <p style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{errors.restaurant}</p>}
                </div>
              </div>

              {/* Message */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Your Message *</label>
                <textarea
                  rows={4} placeholder="What would you like to know? Demo, pricing, or something else..."
                  value={form.message} onChange={e => handleChange('message', e.target.value)}
                  style={{ ...inputStyle('message'), resize: 'vertical', lineHeight: 1.6 }}
                  onFocus={e => e.target.style.borderColor = '#6366f1'}
                  onBlur={e => e.target.style.borderColor = errors.message ? '#f87171' : '#e5e7eb'}
                />
                {errors.message && <p style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{errors.message}</p>}
              </div>

              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={loading}
                style={{ width: '100%', padding: '14px', fontSize: 15, borderRadius: 12, background: loading ? '#818cf8' : '#6366f1', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                {loading ? (
                  <>
                    <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTop: '2px solid #fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                    Submitting...
                  </>
                ) : '🚀 Submit Enquiry'}
              </button>

              <p style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', marginTop: 16 }}>
                Your information is safe. We never spam. 🔒
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const FeatureCard = ({ data, delay }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 32, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(0,0,0,0.10)', transition: { duration: 0.25 } }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{
        background: '#fff', borderRadius: 18, padding: '28px 28px',
        border: `1.5px solid ${hovered ? data.accent + '40' : '#f1f5f9'}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', cursor: 'default',
        transition: 'border-color 0.25s',
        position: 'relative', overflow: 'hidden',
      }}>
      {/* top accent line that slides in on hover */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: hovered ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: data.accent, transformOrigin: 'left', borderRadius: '18px 18px 0 0' }}
      />
      {/* animated icon box */}
      <motion.div
        animate={{ scale: hovered ? 1.12 : 1, rotate: hovered ? 6 : 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 18 }}
        style={{ width: 52, height: 52, background: data.color, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 18 }}>
        {data.icon}
      </motion.div>
      <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', marginBottom: 10 }}>{data.title}</h3>
      <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7 }}>{data.desc}</p>
    </motion.div>
  );
};


/* ══════════════════════════════════════════
   LIVE ANIMATED DASHBOARD MOCKUP
══════════════════════════════════════════ */
const ORDER_POOL = [
  { id: '#1046', item: 'Dal Makhani + 2 items',   status: 'New',     col: '#2563eb', bg: '#eff6ff' },
  { id: '#1045', item: 'Chicken Biryani + 1',     status: 'Cooking', col: '#d97706', bg: '#fffbeb' },
  { id: '#1044', item: 'Paneer Butter Masala',    status: 'Ready',   col: '#16a34a', bg: '#f0fdf4' },
  { id: '#1043', item: 'Veg Thali + 3 items',     status: 'New',     col: '#2563eb', bg: '#eff6ff' },
  { id: '#1042', item: 'Butter Chicken + 2',      status: 'Cooking', col: '#d97706', bg: '#fffbeb' },
  { id: '#1041', item: 'Masala Dosa + Chai',      status: 'Ready',   col: '#16a34a', bg: '#f0fdf4' },
  { id: '#1040', item: 'Paneer Tikka Masala + 2', status: 'New',     col: '#2563eb', bg: '#eff6ff' },
];

const STATUS_CYCLE  = ['New', 'Cooking', 'Ready'];
const STATUS_COLS   = { New: '#2563eb', Cooking: '#d97706', Ready: '#16a34a' };
const STATUS_BGS    = { New: '#eff6ff', Cooking: '#fffbeb', Ready: '#f0fdf4' };
const TABS_LIST     = ['Orders', 'Menu', 'Tables', 'Coupons', 'Reports'];

const LiveDashboardMockup = () => {
  const [visibleOrders, setVisibleOrders] = useState([
    { ...ORDER_POOL[0], uid: 'u0' },
    { ...ORDER_POOL[1], uid: 'u1' },
    { ...ORDER_POOL[2], uid: 'u2' },
  ]);
  const [revenue,    setRevenue]    = useState(48200);
  const [liveOrders, setLiveOrders] = useState(23);
  const [tables,     setTables]     = useState(8);
  const [activeTab,  setActiveTab]  = useState(0);
  const [toast,      setToast]      = useState(null);
  const [revPulse,   setRevPulse]   = useState(false);
  const poolIdx  = useRef(3);
  const uidCount = useRef(3);

  /* New order every 3.5 s */
  useEffect(() => {
    const t = setInterval(() => {
      const src  = ORDER_POOL[poolIdx.current % ORDER_POOL.length];
      poolIdx.current++;
      const newOrder = { ...src, uid: `u${uidCount.current++}` };

      setVisibleOrders(prev => [newOrder, prev[0], prev[1]]);
      setRevenue(r  => r + Math.floor(Math.random() * 550 + 150));
      setLiveOrders(l => l < 39 ? l + 1 : 22);
      setTables(tb  => tb < 14 ? tb + 1 : 6);
      setRevPulse(true);
      setToast(newOrder.id);
      setTimeout(() => setRevPulse(false), 500);
      setTimeout(() => setToast(null),    2400);
    }, 3500);
    return () => clearInterval(t);
  }, []);

  /* Status cycle on middle order every 4.5 s */
  useEffect(() => {
    const t = setInterval(() => {
      setVisibleOrders(prev => prev.map((o, i) => {
        if (i !== 1) return o;
        const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(o.status) + 1) % 3];
        return { ...o, status: next, col: STATUS_COLS[next], bg: STATUS_BGS[next] };
      }));
    }, 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ width: '100%', maxWidth: 440, background: '#fff', borderRadius: 20,
      boxShadow: '0 40px 80px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)',
      overflow: 'visible', position: 'relative' }}>

      {/* ── Toast notification ── */}
      <AnimatePresence>
        {toast && (
          <motion.div
            key="toast"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            style={{ position: 'absolute', top: 50, left: '50%', x: '-50%', zIndex: 30,
              background: '#111827', color: '#fff', fontSize: 11, fontWeight: 600,
              padding: '6px 14px', borderRadius: 999, whiteSpace: 'nowrap',
              boxShadow: '0 4px 20px rgba(0,0,0,0.28)', display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', flexShrink: 0 }} />
            New order {toast} received!
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Browser chrome ── */}
      <div style={{ background: '#0f172a', padding: '13px 18px', display: 'flex', alignItems: 'center', gap: 10, borderRadius: '20px 20px 0 0' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['#ff5f57','#ffbd2e','#28c940'].map(c => (
            <div key={c} style={{ width: 11, height: 11, borderRadius: '50%', background: c }} />
          ))}
        </div>
        <div style={{ flex: 1, background: '#1e293b', borderRadius: 6, height: 22, display: 'flex', alignItems: 'center', paddingLeft: 10 }}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>dashboard.swadzone.com/admin</span>
        </div>
        <div style={{ position: 'relative', width: 9, height: 9, flexShrink: 0 }}>
          <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#22c55e', opacity: 0.5, animation: 'pulse-ring 1.6s ease-out infinite' }} />
          <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#22c55e' }} />
        </div>
      </div>

      {/* inner card with clip */}
      <div style={{ borderRadius: '0 0 20px 20px', overflow: 'hidden' }}>

        {/* ── Stat cards ── */}
        <div style={{ padding: '14px 18px 12px', borderBottom: '1px solid #f1f5f9',
          display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
          {[
            { val: '₹' + revenue.toLocaleString('en-IN'), lbl: 'Today Revenue', bg: '#f0fdf4', col: '#16a34a', pulse: revPulse },
            { val: liveOrders, lbl: 'Live Orders',   bg: '#eff6ff', col: '#2563eb', pulse: revPulse },
            { val: tables,     lbl: 'Tables Active', bg: '#fdf4ff', col: '#9333ea', pulse: false },
          ].map(({ val, lbl, bg, col, pulse: p }) => (
            <motion.div key={lbl}
              animate={p ? { scale: [1, 1.07, 1] } : {}}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              style={{ background: bg, borderRadius: 10, padding: '10px 12px' }}>
              <motion.div
                key={String(val)}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                style={{ fontSize: 16, fontWeight: 700, color: col }}>
                {val}
              </motion.div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{lbl}</div>
            </motion.div>
          ))}
        </div>

        {/* ── Orders list ── */}
        <div style={{ padding: '11px 18px 4px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 8,
            letterSpacing: '0.06em', textTransform: 'uppercase' }}>Recent Orders</div>

          {/* Fixed-height container — no layout shift */}
          <div style={{ position: 'relative', height: 138, overflow: 'hidden' }}>
            <AnimatePresence initial={false} mode="popLayout">
              {visibleOrders.map((o, i) => (
                <motion.div
                  key={o.uid}
                  layout
                  initial={{ opacity: 0, y: -28 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 28 }}
                  transition={{ duration: 0.38, ease: [0.25, 0.46, 0.45, 0.94] }}
                  style={{ position: 'absolute', left: 0, right: 0, top: i * 46,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    paddingBottom: 8, borderBottom: i < 2 ? '1px solid #f3f4f6' : 'none' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{o.id}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>{o.item}</div>
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={o.status}
                      initial={{ opacity: 0, scale: 0.75 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.75 }}
                      transition={{ duration: 0.22 }}
                      style={{ fontSize: 11, fontWeight: 600, color: o.col, background: o.bg,
                        padding: '3px 10px', borderRadius: 999, flexShrink: 0 }}>
                      {o.status}
                    </motion.span>
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div style={{ padding: '8px 18px 14px', display: 'flex', gap: 7 }}>
          {TABS_LIST.map((t, i) => (
            <motion.div key={t}
              onClick={() => setActiveTab(i)}
              whileTap={{ scale: 0.92 }}
              style={{ flex: 1, textAlign: 'center', fontSize: 10, padding: '6px 0',
                borderRadius: 6, cursor: 'pointer', userSelect: 'none',
                background: activeTab === i ? '#0f172a' : '#f9fafb',
                color: activeTab === i ? '#fff' : '#9ca3af',
                fontWeight: activeTab === i ? 600 : 400,
                transition: 'background 0.2s, color 0.2s' }}>
              {t}
            </motion.div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default LandingPage;
